import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { client, urlFor } from '../client'; // Import Sanity client and urlFor
import { PortableText } from '@portabletext/react'; // Import PortableText

// Define reusable components for PortableText serialization if needed
const portableTextComponents = {
  marks: {
    link: ({children, value}) => {
      const rel = !value.href.startsWith('/') ? 'noopener noreferrer' : undefined
      const target = !value.href.startsWith('/') ? '_blank' : undefined
      return (
        <a href={value.href} rel={rel} target={target} className="text-[#691e31] hover:underline">
          {children}
        </a>
      )
    },
  },
  // Customize list rendering if needed
  list: {
    bullet: ({children}) => <ul className="list-disc pl-5 mb-8 space-y-3 text-gray-700">{children}</ul>,
    number: ({children}) => <ol className="list-decimal pl-5 mb-8 space-y-3 text-gray-700">{children}</ol>,
  },
  listItem: {
    bullet: ({children}) => <li>{children}</li>,
    number: ({children}) => <li>{children}</li>,
  },
  // You can add custom block types here if defined in Sanity
};

// Placeholder for loading state
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Placeholder for error state
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600">{message || 'Error loading page content.'}</p>
  </div>
);

const LearnMorePage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const query = `*[_type == "learnMorePage" && _id == "learnMorePageContent"][0]{
      ...,
      bannerImage { asset->{ _id, url } },
      // No need to expand programAreas here if just using title/description
    }`;

    setLoading(true);
    client.fetch(query)
      .then((data) => {
        if (data) {
          setPageData(data);
        } else {
          setError('Learn More Page content not found.');
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching Learn More page data:', err);
        setError('Failed to fetch page content.');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !pageData) {
    return <ErrorMessage message={error || 'Page data is unavailable.'} />;
  }

  // Destructure for easier access, provide fallbacks
  const {
    bannerImage,
    bannerTitle = 'Kindergarten Registration',
    mainHeading = 'Kindergarten Registration Now Open',
    publicationInfo = '',
    introContent = [],
    quoteBlock = {},
    registrationHeading = 'How to Register',
    registrationIntro = '',
    registrationSteps = [],
    documentsHeading = 'Required Documents',
    documentsIntro = '',
    documentsList = [],
    orientationHeading = 'Kindergarten Orientation',
    orientationIntro = [],
    orientationPoints = [],
    orientationRsvp = [],
    programHeading = 'About Our Kindergarten Program',
    programIntro = [],
    programAreas = [],
    programConclusion = [],
    questionsHeading = 'Questions?',
    questionsIntro = [],
    questionsContact = {}
  } = pageData;

  return (
    <div className="bg-white">
      <div className="relative">
        {/* Page Banner */}
        <div className="h-64 md:h-96 overflow-hidden relative">
          {bannerImage?.asset?.url ? (
            <img
              src={urlFor(bannerImage).width(1200).url()} // Use urlFor
              alt={bannerTitle || 'Page Banner'}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-300"></div> // Placeholder if no image
          )}
          <div className="absolute inset-0 bg-black/30 flex items-center">
            <div className="container mx-auto px-4">
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-4">{bannerTitle}</h1>
              {/* Breadcrumbs can be static or dynamic if needed */}
              <div className="flex text-white/90">
                <Link to="/" className="hover:text-yellow-400 transition-colors">Home</Link>
                <span className="mx-2">/</span>
                <span>{bannerTitle}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            {/* Consider making the tag dynamic if needed */}
            <span className="inline-block bg-[#691e31]/10 text-[#691e31] px-3 py-1 text-sm font-medium rounded-full mb-4">2025-26 School Year</span>
            <h2 className="text-[#2a4865] text-3xl font-bold mb-6">{mainHeading}</h2>
            {publicationInfo && (
                <p className="text-gray-500 text-sm mb-6">{publicationInfo}</p>
            )}

            <div className="prose max-w-none">
              {introContent && introContent.length > 0 && (
                <div className="text-gray-700 mb-6 leading-relaxed">
                  <PortableText value={introContent} components={portableTextComponents} />
                </div>
              )}

              {quoteBlock?.quoteText && (
                <div className="my-8 border-l-4 border-[#691e31] pl-6 py-2 bg-gray-50">
                  <p className="text-lg font-medium text-[#2a4865]">
                    "{quoteBlock.quoteText}"
                  </p>
                  {quoteBlock.attribution && (
                    <p className="text-sm text-gray-600 mt-2">{quoteBlock.attribution}</p>
                  )}
                </div>
              )}

              {/* Registration Section */}
              <h3 className="text-[#2a4865] text-xl font-bold mt-10 mb-4">{registrationHeading}</h3>
              {registrationIntro && (
                <p className="text-gray-700 mb-4">{registrationIntro}</p>
              )}
              {registrationSteps && registrationSteps.length > 0 && (
                 <PortableText value={registrationSteps} components={portableTextComponents} />
              )}

              {/* Documents Section */}
              <h3 className="text-[#2a4865] text-xl font-bold mt-10 mb-4">{documentsHeading}</h3>
              {documentsIntro && (
                <p className="text-gray-700 mb-4">{documentsIntro}</p>
              )}
              {documentsList && documentsList.length > 0 && (
                <ul className="list-disc pl-5 mb-8 space-y-3 text-gray-700">
                  {documentsList.map((item, index) => <li key={index}>{item}</li>)}
                </ul>
              )}

              {/* Orientation Section */}
              {orientationHeading && (
                <div className="bg-[#2a4865]/5 p-6 rounded-lg mb-8">
                  <h3 className="text-[#2a4865] text-xl font-bold mb-4">{orientationHeading}</h3>
                  {orientationIntro && orientationIntro.length > 0 && (
                    <div className="text-gray-700 mb-4">
                        <PortableText value={orientationIntro} components={portableTextComponents} />
                    </div>
                  )}
                  {orientationPoints && orientationPoints.length > 0 && (
                    <ul className="list-disc pl-5 mb-4 space-y-2 text-gray-700">
                      {orientationPoints.map((item, index) => <li key={index}>{item}</li>)}
                    </ul>
                  )}
                  {orientationRsvp && orientationRsvp.length > 0 && (
                    <div className="text-gray-700">
                       <PortableText value={orientationRsvp} components={portableTextComponents} />
                    </div>
                  )}
                </div>
              )}

              {/* Program Section */}
              <h3 className="text-[#2a4865] text-xl font-bold mt-10 mb-4">{programHeading}</h3>
              {programIntro && programIntro.length > 0 && (
                <div className="text-gray-700 mb-4">
                   <PortableText value={programIntro} components={portableTextComponents} />
                </div>
              )}
              {programAreas && programAreas.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {programAreas.map((area) => (
                    <div key={area._key} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                      <h4 className="text-[#2a4865] font-bold mb-2">{area.areaTitle}</h4>
                      <p className="text-gray-700 text-sm">{area.areaDescription}</p>
                    </div>
                  ))}
                </div>
              )}
              {programConclusion && programConclusion.length > 0 && (
                <div className="text-gray-700 mb-8">
                  <PortableText value={programConclusion} components={portableTextComponents} />
                </div>
              )}

              {/* Questions Section */}
              <h3 className="text-[#2a4865] text-xl font-bold mt-10 mb-4">{questionsHeading}</h3>
              {questionsIntro && questionsIntro.length > 0 && (
                <div className="text-gray-700 mb-4">
                  <PortableText value={questionsIntro} components={portableTextComponents} />
                </div>
              )}
              {questionsContact?.contactName && (
                <div className="bg-gray-50 p-6 rounded-lg mb-8">
                  {questionsContact.contactName && <p className="mb-2"><strong className="text-[#2a4865]">{questionsContact.contactName}</strong></p>}
                  {questionsContact.contactTitle && <p className="mb-2">{questionsContact.contactTitle}</p>}
                  {questionsContact.contactPhone && <p className="mb-2">Phone: {questionsContact.contactPhone}</p>}
                  {questionsContact.contactEmail && <p>Email: <a href={`mailto:${questionsContact.contactEmail}`} className="text-[#691e31] hover:underline">{questionsContact.contactEmail}</a></p>}
                </div>
              )}

              {/* Static Return to Home button */}
              <div className="flex justify-center mt-10">
                <Link to="/" className="inline-flex items-center bg-[#691e31] text-white hover:bg-[#7f2a3e] font-semibold px-6 py-3 rounded transition-colors group">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Return to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnMorePage; 